import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useClienteAuthStore } from '@/stores/clienteAuthStore';
import { CLIENTE_MOBILE_APP_VERSION } from '@/constants/clienteMobileAppVersion';

// Em dev, sempre o origin do Vite (ex.: :3004) para passar pelo proxy /api → API real (ex.: :3101).
// VITE_API_URL=localhost:3001 no .env quebraria login da área do cliente após troca de porta da API.
const viteEnv = (import.meta as any).env as { DEV?: boolean; VITE_API_URL?: string } | undefined;
const apiOrigin =
  viteEnv?.DEV && typeof window !== 'undefined'
    ? window.location.origin
    : (viteEnv?.VITE_API_URL ?? (typeof window !== 'undefined' ? window.location.origin : ''));

const API_V1_BASE = `${apiOrigin}/api/v1`;
export const MOBILE_API_BASE = `${API_V1_BASE}/mobile`;

function isPublicMobileAuthUrl(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0] ?? url;
  const publicPaths = new Set([
    '/auth/login',
    '/auth/register',
    '/auth/preview-exchange',
    '/auth/forgot-password',
    '/auth/request-reset-code',
    '/auth/verify-reset-code',
    '/auth/reset-password',
    '/auth/verify-cpf',
    '/auth/request-first-access-code',
    '/auth/verify-first-access-code',
    '/auth/documentos/versoes',
    '/app-version',
    '/zehmotoca/instagram-feed',
  ]);
  if (publicPaths.has(path)) return true;
  if (path.startsWith('/auth/verify-email/')) return true;
  return false;
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (t: string) => void; reject: (e: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else if (token) p.resolve(token);
  });
  failedQueue = [];
}

export const mobileApi: AxiosInstance = axios.create({
  baseURL: MOBILE_API_BASE,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Version': CLIENTE_MOBILE_APP_VERSION,
  },
});

mobileApi.interceptors.request.use((config) => {
  const path = config.url?.split('?')[0];
  if (path && isPublicMobileAuthUrl(path)) {
    delete config.headers.Authorization;
    return config;
  }

  const token = useClienteAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

mobileApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const url = originalRequest?.url;

    if ((status === 401 || status === 403) && !originalRequest._retry && !isPublicMobileAuthUrl(url)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return mobileApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = useClienteAuthStore.getState().refreshToken;
      if (!refreshToken) {
        isRefreshing = false;
        useClienteAuthStore.getState().logout();
        processQueue(new Error('no refresh'), null);
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${MOBILE_API_BASE}/auth/refresh`, { refreshToken });
        const body = response.data?.data ?? response.data;
        const accessToken = body.accessToken as string;
        const newRefresh = (body.refreshToken as string) || refreshToken;
        if (!accessToken) throw new Error('Sem accessToken no refresh');

        useClienteAuthStore.getState().setTokens(accessToken, newRefresh);
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        processQueue(null, accessToken);
        isRefreshing = false;
        return mobileApi(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        isRefreshing = false;
        useClienteAuthStore.getState().logout();
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);
